import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle, EnvelopeSimple, ShieldCheck, Sparkle, HourglassMedium } from '@phosphor-icons/react';
import IndieLawLogo from '../components/IndieLawLogo';

import API_URL from '../config';

export default function GetStartedPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Beta access state
  const [beta, setBeta] = useState(null); // { limit, remaining, open }
  const [waitSubmitted, setWaitSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/beta-status`)
      .then((res) => setBeta(res.data))
      .catch(() => setBeta({ open: true, remaining: null, limit: null }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/public/client-intake`, { name, email, website });
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/public/beta-waitlist`, { name, email, website });
      setWaitSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const betaFull = beta && beta.open === false;

  return (
    <div className="min-h-[100svh] flex flex-col lg:flex-row bg-white">
      {/* Left brand panel */}
      <div
        className="lg:w-1/2 px-6 py-5 lg:p-16 flex flex-col justify-between"
        style={{ backgroundColor: '#141b4d' }}
      >
        <IndieLawLogo variant="light" className="h-9 lg:h-20 w-auto" />
        <div className="hidden lg:block max-w-md">
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Get access to your<br />
            <span style={{ color: '#ffd100' }}>trademark portal</span>
          </h1>
          <p className="text-base mt-5 leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
            Use the email you sign up with to unlock a private dashboard where you can follow your onboarding
            steps, read your trademark search report, and track every application and registration — in one place.
          </p>
          <div className="mt-8 space-y-3">
            {[
              'Live status for all your applications & registrations',
              'Accurate USPTO deadlines, tracked for you',
              'Your documents and matters, always in one place',
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-3">
                <ShieldCheck weight="duotone" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#ffd100' }} />
                <span className="text-sm" style={{ color: 'rgba(255,255,255,0.85)' }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs hidden lg:block" style={{ color: 'rgba(255,255,255,0.4)' }}>
          © {new Date().getFullYear()} Indie Law. All rights reserved.
        </p>
      </div>

      {/* Right form panel */}
      <div className="lg:w-1/2 flex items-start lg:items-center justify-center px-6 lg:px-16 pt-8 lg:pt-16 pb-[calc(env(safe-area-inset-bottom)+3rem)] lg:pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {submitted ? (
            <div className="text-center" data-testid="get-started-success">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(34,197,94,0.12)' }}>
                <CheckCircle weight="duotone" className="w-8 h-8" style={{ color: '#22C55E' }} />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#141b4d', fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Check your email
              </h2>
              <p className="mb-6 leading-relaxed" style={{ color: '#52525B' }}>
                If your trademark matters are in our system, we've just emailed you a secure link to set your
                password and log in. If you don't see it in a few minutes, check your spam folder or email us at{' '}
                <a href="mailto:clients@indielaw.com" className="font-medium" style={{ color: '#1d57a5' }}>
                  clients@indielaw.com
                </a>.
              </p>
              <Link to="/login" className="btn-primary inline-block" data-testid="success-back-to-login">
                Go to Login
              </Link>
            </div>
          ) : waitSubmitted ? (
            <div className="text-center" data-testid="waitlist-success">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(255,209,0,0.18)' }}>
                <CheckCircle weight="duotone" className="w-8 h-8" style={{ color: '#141b4d' }} />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#141b4d', fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                You're on the list
              </h2>
              <p className="mb-6 leading-relaxed" style={{ color: '#52525B' }}>
                Thanks! We've added you to the beta waitlist and will email you the moment a spot opens up. In the
                meantime, keep reaching us the usual way at{' '}
                <a href="mailto:clients@indielaw.com" className="font-medium" style={{ color: '#1d57a5' }}>clients@indielaw.com</a>.
              </p>
              <Link to="/login" className="btn-secondary inline-block" data-testid="waitlist-back-to-login">
                Go to Login
              </Link>
            </div>
          ) : betaFull ? (
            <div data-testid="beta-full">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-2 py-1 mb-4" style={{ backgroundColor: '#141b4d', color: '#ffd100' }}>
                <HourglassMedium weight="fill" className="w-3.5 h-3.5" /> Beta is full
              </span>
              <h2 className="text-3xl font-bold mb-2" style={{ color: '#141b4d', fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                We're at capacity right now
              </h2>
              <p className="mb-6 leading-relaxed" style={{ color: '#52525B' }}>
                We're rolling this portal out to a small group of clients at a time so we can get it right. All the
                current beta spots are taken — leave your email and we'll invite you as soon as the next spot opens.
              </p>
              <form onSubmit={handleWaitlist} className="space-y-5" data-testid="waitlist-form">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="waitlist-error">{error}</div>
                )}
                <div style={{ position: 'absolute', left: '-9999px', top: 'auto' }} aria-hidden="true">
                  <label>Website<input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
                </div>
                <div>
                  <label className="label-uppercase block mb-2">Full Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input-swiss" placeholder="Jane Doe" required data-testid="waitlist-name-input" />
                </div>
                <div>
                  <label className="label-uppercase block mb-2">Email Address</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-swiss" placeholder="you@example.com" required data-testid="waitlist-email-input" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="waitlist-submit-btn">
                  <EnvelopeSimple weight="bold" className="w-4 h-4" />
                  {loading ? 'Adding you…' : 'Join the Waitlist'}
                </button>
              </form>
              <p className="mt-6 text-center text-sm" style={{ color: '#9a9488' }}>
                Already have access?{' '}
                <Link to="/login" className="font-medium hover:underline" style={{ color: '#141b4d' }}>Log in</Link>
              </p>
            </div>
          ) : (
            <>
              <p className="label-uppercase mb-1" style={{ color: '#1d57a5' }}>Client Portal</p>
              <h2 className="text-3xl font-bold mb-2" style={{ color: '#141b4d', fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Get Started
              </h2>
              <p className="mb-4 leading-relaxed" style={{ color: '#52525B' }}>
                Enter your name and the email you use with Indie Law. We'll find your filed trademarks and send you
                a link to log in.
              </p>

              {/* Beta notice + live spots remaining */}
              <div
                className="mb-5 p-4"
                style={{ backgroundColor: '#f2e9db', borderLeft: '3px solid #ffd100' }}
                data-testid="beta-notice"
              >
                <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider mb-1.5" style={{ color: '#141b4d' }}>
                  <Sparkle weight="fill" className="w-3.5 h-3.5" /> We're in beta
                </p>
                <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
                  We're rolling this out to a small group of clients first so we can get it right, so please don't rely
                  on the portal alone yet — keep email as your source of truth. If anything's confusing or broken, tell
                  us so we can fix it fast.
                  {beta && typeof beta.remaining === 'number' && (
                    <span className="block mt-2 font-semibold" style={{ color: '#141b4d' }} data-testid="beta-spots-remaining">
                      {beta.remaining} of {beta.limit} beta spot{beta.limit === 1 ? '' : 's'} remaining.
                    </span>
                  )}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="get-started-form">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="get-started-error">
                    {error}
                  </div>
                )}

                {/* Honeypot — visually hidden, off-screen; bots fill it, humans don't */}
                <div style={{ position: 'absolute', left: '-9999px', top: 'auto' }} aria-hidden="true">
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </label>
                </div>

                <div>
                  <label className="label-uppercase block mb-2">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-swiss"
                    placeholder="Jane Doe"
                    required
                    data-testid="get-started-name-input"
                  />
                </div>

                <div>
                  <label className="label-uppercase block mb-2">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-swiss"
                    placeholder="you@example.com"
                    required
                    data-testid="get-started-email-input"
                  />
                  <p className="text-xs mt-2" style={{ color: '#9a9488' }}>
                    Use the same email you've been corresponding with us on.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="get-started-submit-btn"
                >
                  <EnvelopeSimple weight="bold" className="w-4 h-4" />
                  {loading ? 'Sending...' : 'Send Me My Access Link'}
                </button>
              </form>

              <p className="mt-6 text-center text-sm" style={{ color: '#9a9488' }}>
                Already have access?{' '}
                <Link to="/login" className="font-medium hover:underline" style={{ color: '#141b4d' }} data-testid="get-started-login-link">
                  Log in
                </Link>
              </p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
